import { useState } from 'react';
import { api, can, getUser } from '../lib/api';
import { dateTime } from '../lib/format';
import { Card, Empty, ErrorNote, Field, Loading, Modal, Pill, useResource, useToast } from '../components/ui';

/**
 * Developer page cards for server-to-server integrations: the request log,
 * idempotency, IP allow-lists and the sandbox. Reading takes the security
 * screen; changing anything takes security:edit (owner and IT administrator by
 * default). The API refuses the same actions to every other role.
 */

type Env = 'live' | 'test';
const ENV_LABEL: Record<Env, string> = { live: 'Live', test: 'Test' };

interface KeyRow { id: string; name: string; prefix: string; scopes: string[]; status: 'active' | 'revoked'; environment: Env; createdAt: string; lastUsedAt: string | null }

/* ------------------------------------------------------------- request log */

interface LogRow {
  requestId: string; clientRequestId: string | null; environment: Env; keyName: string | null; keyPrefix: string | null;
  method: string; route: string; status: number; durationMs: number; ipMasked: string | null; replayed: boolean;
  errorCode: string | null; at: string;
}

interface LogFilters { status: string; environment: '' | Env; clientRequestId: string; from: string; to: string }

const EMPTY_FILTERS: LogFilters = { status: '', environment: '', clientRequestId: '', from: '', to: '' };

function statusTone(status: number): 'ok' | 'warn' | 'bad' | undefined {
  if (status < 300) return 'ok';
  if (status < 500) return 'warn';
  return 'bad';
}

export function RequestLogCard() {
  const [filters, setFilters] = useState<LogFilters>(EMPTY_FILTERS);
  const [applied, setApplied] = useState<LogFilters>(EMPTY_FILTERS);
  const [pages, setPages] = useState<string[]>([]);
  const before = pages[pages.length - 1];
  const qs = new URLSearchParams();
  if (applied.status) qs.set('status', applied.status);
  if (applied.environment) qs.set('environment', applied.environment);
  if (applied.clientRequestId.trim()) qs.set('clientRequestId', applied.clientRequestId.trim());
  if (applied.from) qs.set('from', new Date(`${applied.from}T00:00:00`).toISOString());
  if (applied.to) qs.set('to', new Date(`${applied.to}T23:59:59.999`).toISOString());
  if (before) qs.set('before', before);
  qs.set('limit', '50');
  const query = qs.toString();
  const log = useResource<{ rows: LogRow[]; next: string | null }>(() => api.get(`/v1/api-log?${query}`), [query]);

  function apply() {
    setPages([]);
    setApplied(filters);
  }

  return (
    <Card title="Request log">
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 10, alignItems: 'end' }}>
        <Field label="Status">
          <select value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })}>
            <option value="">All</option>
            <option value="2xx">2xx</option>
            <option value="4xx">4xx</option>
            <option value="5xx">5xx</option>
            <option value="403">403</option>
            <option value="409">409</option>
            <option value="422">422</option>
            <option value="429">429</option>
          </select>
        </Field>
        <Field label="Environment">
          <select value={filters.environment} onChange={(e) => setFilters({ ...filters, environment: e.target.value as LogFilters['environment'] })}>
            <option value="">Both</option>
            <option value="live">Live</option>
            <option value="test">Test</option>
          </select>
        </Field>
        <Field label="Client request ID">
          <input type="text" value={filters.clientRequestId} onChange={(e) => setFilters({ ...filters, clientRequestId: e.target.value })} />
        </Field>
        <Field label="From">
          <input type="date" value={filters.from} onChange={(e) => setFilters({ ...filters, from: e.target.value })} />
        </Field>
        <Field label="To">
          <input type="date" value={filters.to} onChange={(e) => setFilters({ ...filters, to: e.target.value })} />
        </Field>
        <div className="field" style={{ display: 'flex', gap: 8 }}>
          <button className="primary small" onClick={apply}>Filter</button>
          <button className="small" onClick={() => { setFilters(EMPTY_FILTERS); setPages([]); setApplied(EMPTY_FILTERS); }}>Clear</button>
        </div>
      </div>

      <ErrorNote error={log.error} />
      {log.loading && !log.data && <Loading />}
      {log.data && log.data.rows.length === 0 && <Empty>No calls.</Empty>}
      {log.data && log.data.rows.length > 0 && (
        <div className="table-scroll">
          <table className="data">
            <thead>
              <tr><th>Time</th><th>Key</th><th>Call</th><th>Status</th><th>Duration</th><th>Client request ID</th><th>Request ID</th><th>Network</th></tr>
            </thead>
            <tbody>
              {log.data.rows.map((r) => (
                <tr key={r.requestId}>
                  <td className="mono small">{dateTime(r.at)}</td>
                  <td className="small">
                    {r.keyName ?? 'Unknown key'} <Pill tone={r.environment === 'test' ? 'brand' : undefined}>{ENV_LABEL[r.environment]}</Pill>
                    {r.keyPrefix && <div className="mono small muted">{r.keyPrefix}…</div>}
                  </td>
                  <td className="mono small">{r.method} {r.route}</td>
                  <td>
                    <Pill tone={statusTone(r.status)}>{r.status}</Pill>
                    {r.errorCode && <div className="mono small muted">{r.errorCode}</div>}
                    {r.replayed && <div className="small muted">Replayed</div>}
                  </td>
                  <td className="small">{r.durationMs} ms</td>
                  <td className="mono small">{r.clientRequestId ?? ''}</td>
                  <td className="mono small">{r.requestId}</td>
                  <td className="mono small">{r.ipMasked ?? ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
        <button className="small" disabled={pages.length === 0} onClick={() => setPages((p) => p.slice(0, -1))}>Newer</button>
        <button className="small" disabled={!log.data?.next} onClick={() => log.data?.next && setPages((p) => [...p, log.data!.next!])}>Older</button>
        <span className="small muted" style={{ marginInlineStart: 'auto' }}>Service-key calls. Kept 30 days. Addresses cut to /24 (IPv4) or /48 (IPv6). No request or response bodies.</span>
      </div>
    </Card>
  );
}

/* ------------------------------------------------------------- idempotency */

interface ApiSettings { requireIdempotencyKey: boolean; idempotencyWindowHours: number; logRetentionDays: number }

export function IdempotencyCard() {
  const toast = useToast();
  const canEdit = can(getUser(), 'security:edit');
  const res = useResource<ApiSettings>(() => api.get('/v1/settings/api'));
  const [busy, setBusy] = useState(false);

  async function toggle(next: boolean) {
    setBusy(true);
    try {
      await api.put('/v1/settings/api', { requireIdempotencyKey: next });
      toast(next ? 'Idempotency keys required.' : 'Idempotency keys optional.', 'ok');
      res.reload();
    } catch (err) {
      toast((err as Error).message, 'bad');
    } finally {
      setBusy(false);
    }
  }

  const s = res.data;
  return (
    <Card title="Idempotency">
      <ErrorNote error={res.error} />
      {s && (
        <>
          <dl className="kv">
            <dt>Header</dt><dd className="mono small">Idempotency-Key</dd>
            <dt>Applies to</dt><dd>Every POST made with a service key</dd>
            <dt>Kept</dt><dd>{s.idempotencyWindowHours} hours</dd>
            <dt>Status</dt><dd>{s.requireIdempotencyKey ? <Pill tone="ok">Required</Pill> : <Pill>Optional</Pill>}</dd>
          </dl>
          <p className="small muted" style={{ margin: '10px 0 0' }}>
            Same key and same request: the first response again, with Idempotent-Replayed: true. Same key, different request: 422. While the first is running: 409.
          </p>
          {canEdit && (
            <label className="small" style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 10 }}>
              <input type="checkbox" style={{ width: 'auto' }} checked={s.requireIdempotencyKey} disabled={busy} onChange={(e) => toggle(e.target.checked)} />
              Refuse service-key POSTs without an Idempotency-Key
            </label>
          )}
        </>
      )}
    </Card>
  );
}

/* ------------------------------------------------------------- IP allow-list */

interface AllowEntry { id: string; keyId: string | null; keyName: string | null; environment: Env; network: string; label: string; createdBy: string; createdAt: string }

export function IpAllowListCard({ hasSandbox }: { hasSandbox: boolean }) {
  const toast = useToast();
  const canEdit = can(getUser(), 'security:edit');
  const list = useResource<{ entries: AllowEntry[] }>(() => api.get('/v1/ip-allow-list'));
  const [env, setEnv] = useState<Env>('live');
  const keys = useResource<{ keys: KeyRow[] }>(
    () => (canEdit && (env === 'live' || hasSandbox) ? api.get(`/v1/service-keys?environment=${env}`) : Promise.resolve({ keys: [] })),
    [env, hasSandbox],
  );
  const [network, setNetwork] = useState('');
  const [keyId, setKeyId] = useState('');
  const [label, setLabel] = useState('');
  const [busy, setBusy] = useState(false);

  async function add() {
    setBusy(true);
    try {
      await api.post('/v1/ip-allow-list', { network: network.trim(), environment: env, keyId: keyId || null, label: label.trim() || undefined });
      toast('Added to the allow-list.', 'ok');
      setNetwork('');
      setLabel('');
      list.reload();
    } catch (err) {
      toast((err as Error).message, 'bad');
    } finally {
      setBusy(false);
    }
  }

  async function remove(e: AllowEntry) {
    if (!window.confirm(`Remove ${e.network}? Calls from it are refused if the list still has other entries.`)) return;
    try {
      await api.delete(`/v1/ip-allow-list/${e.id}`);
      toast('Removed.', 'ok');
      list.reload();
    } catch (err) {
      toast((err as Error).message, 'bad');
    }
  }

  const entries = list.data?.entries ?? [];
  return (
    <Card title="IP allow-list">
      <p className="small muted" style={{ marginTop: 0 }}>
        With no entries, service keys work from any address. A call passes when its address is on each list that has entries: the environment list and the key's own list. Others get 403 ip_not_allowed.
      </p>
      <ErrorNote error={list.error} />
      {list.data && entries.length === 0 && <Empty>No entries. Any address may call.</Empty>}
      {entries.length > 0 && (
        <div className="table-scroll">
          <table className="data">
            <thead><tr><th>Network</th><th>Environment</th><th>Applies to</th><th>Label</th><th>Added</th><th /></tr></thead>
            <tbody>
              {entries.map((e) => (
                <tr key={e.id}>
                  <td className="mono">{e.network}</td>
                  <td><Pill tone={e.environment === 'test' ? 'brand' : undefined}>{ENV_LABEL[e.environment]}</Pill></td>
                  <td className="small">{e.keyName ?? `Every ${e.environment} key`}</td>
                  <td className="small">{e.label}</td>
                  <td className="small muted">{dateTime(e.createdAt)} · {e.createdBy}</td>
                  <td style={{ textAlign: 'right' }}>{canEdit && <button className="small" onClick={() => remove(e)}>Remove</button>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {canEdit && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10, alignItems: 'end', marginTop: 12 }}>
          <Field label="Address or network" help="203.0.113.0/24, 198.51.100.7 or 2001:db8:1234::/48">
            <input type="text" value={network} onChange={(e) => setNetwork(e.target.value)} />
          </Field>
          <Field label="Environment">
            <select value={env} onChange={(e) => { setEnv(e.target.value as Env); setKeyId(''); }}>
              <option value="live">Live</option>
              {hasSandbox && <option value="test">Test</option>}
            </select>
          </Field>
          <Field label="Applies to">
            <select value={keyId} onChange={(e) => setKeyId(e.target.value)}>
              <option value="">Every {env} key</option>
              {(keys.data?.keys ?? []).filter((k) => k.status === 'active').map((k) => <option key={k.id} value={k.id}>{k.name} ({k.prefix}…)</option>)}
            </select>
          </Field>
          <Field label="Label">
            <input type="text" value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Mumbai data centre" />
          </Field>
          <div className="field">
            <button className="primary" onClick={add} disabled={busy || !network.trim()}>{busy ? 'Adding…' : 'Add'}</button>
          </div>
        </div>
      )}
    </Card>
  );
}

/* ------------------------------------------------------------- sandbox */

interface SandboxSummary {
  name: string; createdAt: string; people: number; events: number; testKeys: number; webhooks: number;
  purposes: Array<{ key: string; name: string; status: string }>;
  collectionPoints: Array<{ name: string; channel: string; status: string }>;
}

interface WidgetKey { collectionPoint: string; channel: string; key: string }

const TEST_SCOPES: Array<[string, string]> = [
  ['consent:capture', 'Record consent and simulate'],
  ['consent:check', 'Check consent'],
  ['consent:read', 'Read consent state'],
  ['suppression:read', 'Pull the suppression list'],
  ['export:read', 'Download exports'],
];

export function SandboxCard({ onChange }: { onChange: () => void }) {
  const toast = useToast();
  const canEdit = can(getUser(), 'security:edit');
  const [tick, setTick] = useState(0);
  const res = useResource<{ sandbox: SandboxSummary | null }>(() => api.get('/v1/sandbox'), [tick]);
  const sandbox = res.data?.sandbox ?? null;
  const keys = useResource<{ keys: KeyRow[] }>(
    () => (sandbox && canEdit ? api.get('/v1/service-keys?environment=test') : Promise.resolve({ keys: [] })),
    [Boolean(sandbox), tick],
  );
  const hooks = useResource<{ subscriptions: Array<{ id: string; name: string; url: string; events: string[]; status: string }> }>(
    () => (sandbox ? api.get('/v1/sandbox/webhooks') : Promise.resolve({ subscriptions: [] })),
    [Boolean(sandbox), tick],
  );
  const [busy, setBusy] = useState<string | null>(null);
  const [widgetKeys, setWidgetKeys] = useState<WidgetKey[] | null>(null);
  const [issuing, setIssuing] = useState(false);
  const [keyName, setKeyName] = useState('');
  const [scopes, setScopes] = useState<string[]>(['consent:capture', 'consent:check']);
  const [issued, setIssued] = useState<{ name: string; key: string } | null>(null);
  const [hookForm, setHookForm] = useState<{ name: string; url: string } | null>(null);
  const [hookSecret, setHookSecret] = useState<string | null>(null);
  const [resetting, setResetting] = useState(false);
  const [confirmText, setConfirmText] = useState('');

  const refresh = () => { setTick((t) => t + 1); onChange(); };

  async function run(label: string, fn: () => Promise<void>) {
    setBusy(label);
    try {
      await fn();
    } catch (err) {
      toast((err as Error).message, 'bad');
    } finally {
      setBusy(null);
    }
  }

  const create = () => run('create', async () => {
    const out = await api.post<{ widgetKeys: WidgetKey[] }>('/v1/sandbox');
    setWidgetKeys(out.widgetKeys);
    toast('Sandbox created.', 'ok');
    refresh();
  });

  const issue = () => run('issue', async () => {
    const out = await api.post<{ name: string; key: string }>('/v1/service-keys', { name: keyName.trim(), scopes, environment: 'test' });
    setIssued({ name: out.name, key: out.key });
    setIssuing(false);
    setKeyName('');
    refresh();
  });

  const revoke = (k: KeyRow) => {
    if (!window.confirm(`Revoke "${k.name}"? Tests using it stop working at once.`)) return;
    void run('revoke', async () => {
      await api.post(`/v1/service-keys/${k.id}/revoke`);
      toast('Test key revoked.', 'ok');
      refresh();
    });
  };

  const addHook = () => run('hook', async () => {
    if (!hookForm) return;
    const out = await api.post<{ secret: string }>('/v1/sandbox/webhooks', {
      name: hookForm.name.trim(), url: hookForm.url.trim(), events: ['granted', 'declined', 'withdrawn', 'expired'],
    });
    setHookSecret(out.secret);
    setHookForm(null);
    refresh();
  });

  const reset = () => run('reset', async () => {
    const out = await api.post<{ reset: { events: number; people: number } }>('/v1/sandbox/reset', { confirm: 'reset' });
    toast(`Sandbox reset: ${out.reset.people} people and ${out.reset.events} ledger events deleted.`, 'ok');
    setResetting(false);
    setConfirmText('');
    refresh();
  });

  return (
    <Card title="Sandbox">
      <p className="small muted" style={{ marginTop: 0 }}>
        A separate copy of this organisation's purposes, notices and collection points, with its own ledger and no people. Test keys start wk_test_ and reach only the sandbox. Live keys cannot reach it.
      </p>
      <ErrorNote error={res.error} />
      {res.loading && !res.data && <Loading />}

      {res.data && !sandbox && (
        <>
          <Empty>No sandbox.</Empty>
          {canEdit && <button className="primary" onClick={create} disabled={busy !== null}>{busy === 'create' ? 'Creating…' : 'Create sandbox'}</button>}
        </>
      )}

      {sandbox && (
        <>
          <dl className="kv">
            <dt>Name</dt><dd>{sandbox.name}</dd>
            <dt>Created</dt><dd>{dateTime(sandbox.createdAt)}</dd>
            <dt>People</dt><dd>{sandbox.people}</dd>
            <dt>Ledger events</dt><dd>{sandbox.events}</dd>
            <dt>Purposes</dt><dd className="small">{sandbox.purposes.map((p) => p.name).join(', ') || 'None'}</dd>
            <dt>Collection points</dt><dd className="small">{sandbox.collectionPoints.map((c) => c.name).join(', ') || 'None'}</dd>
          </dl>

          <h4 style={{ margin: '16px 0 6px' }}>Test keys</h4>
          {canEdit ? (
            <>
              {(keys.data?.keys ?? []).length === 0 ? <Empty>No test keys.</Empty> : (
                <div className="table-scroll">
                  <table className="data">
                    <thead><tr><th>Name</th><th>Prefix</th><th>Last used</th><th>Status</th><th /></tr></thead>
                    <tbody>
                      {keys.data!.keys.map((k) => (
                        <tr key={k.id}>
                          <td><strong>{k.name}</strong><div className="small muted">Issued {dateTime(k.createdAt)}</div></td>
                          <td className="mono small">{k.prefix}…</td>
                          <td className="small muted">{k.lastUsedAt ? dateTime(k.lastUsedAt) : 'Never'}</td>
                          <td>{k.status === 'active' ? <Pill tone="ok">Active</Pill> : <Pill>Revoked</Pill>}</td>
                          <td style={{ textAlign: 'right' }}>{k.status === 'active' && <button className="small" onClick={() => revoke(k)}>Revoke</button>}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              <button className="small" style={{ marginTop: 8 }} onClick={() => setIssuing(true)}>Issue test key</button>
            </>
          ) : <p className="small">{sandbox.testKeys} active</p>}

          <h4 style={{ margin: '16px 0 6px' }}>Sandbox webhooks</h4>
          {(hooks.data?.subscriptions ?? []).length === 0 ? <Empty>None. Simulated events reach only the endpoints listed here.</Empty> : (
            <div className="table-scroll">
              <table className="data">
                <thead><tr><th>Name</th><th>Endpoint</th><th>Events</th><th>Status</th></tr></thead>
                <tbody>
                  {hooks.data!.subscriptions.map((s) => (
                    <tr key={s.id}>
                      <td>{s.name}</td>
                      <td className="mono small" style={{ wordBreak: 'break-all' }}>{s.url}</td>
                      <td className="small">{s.events.join(', ')}</td>
                      <td><Pill tone={s.status === 'active' ? 'ok' : undefined}>{s.status === 'active' ? 'Active' : s.status === 'paused' ? 'Paused' : 'Disabled'}</Pill></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {canEdit && !hookForm && <button className="small" style={{ marginTop: 8 }} onClick={() => setHookForm({ name: '', url: '' })}>Add sandbox webhook</button>}
          {hookForm && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10, alignItems: 'end', marginTop: 8 }}>
              <Field label="Name"><input type="text" value={hookForm.name} onChange={(e) => setHookForm({ ...hookForm, name: e.target.value })} /></Field>
              <Field label="Endpoint" help="HTTPS address of your test receiver"><input type="url" value={hookForm.url} onChange={(e) => setHookForm({ ...hookForm, url: e.target.value })} /></Field>
              <div className="field" style={{ display: 'flex', gap: 8 }}>
                <button onClick={() => setHookForm(null)}>Cancel</button>
                <button className="primary" onClick={addHook} disabled={busy !== null || !hookForm.name.trim() || !hookForm.url.trim()}>Add</button>
              </div>
            </div>
          )}

          <h4 style={{ margin: '16px 0 6px' }}>Simulate</h4>
          <p className="small" style={{ margin: 0 }}>
            With a test key: <span className="mono">POST /v1/sandbox/simulate/decision</span>, <span className="mono">/withdraw</span> and <span className="mono">/expire</span>. Each writes to the sandbox ledger through the same code as live, and sandbox webhooks fire.
          </p>

          {canEdit && (
            <div style={{ marginTop: 16 }}>
              <button className="danger" onClick={() => setResetting(true)} disabled={busy !== null}>Reset sandbox</button>
            </div>
          )}
        </>
      )}

      {widgetKeys && (
        <Modal title="Sandbox collection point keys" description="Shown once. Publishable keys for the sandbox copy of each collection point." onClose={() => setWidgetKeys(null)}
          footer={<button className="primary" onClick={() => setWidgetKeys(null)}>Done</button>}>
          {widgetKeys.length === 0 ? <Empty>No collection points to copy.</Empty> : widgetKeys.map((w) => (
            <div key={w.key} style={{ marginBottom: 10 }}>
              <strong>{w.collectionPoint}</strong> <span className="small muted">{w.channel}</span>
              <pre className="mono keyblock">{w.key}</pre>
            </div>
          ))}
        </Modal>
      )}

      {issuing && (
        <Modal title="Issue test key" onClose={() => setIssuing(false)}
          footer={<><button onClick={() => setIssuing(false)}>Cancel</button><button className="primary" onClick={issue} disabled={busy !== null || !keyName.trim() || scopes.length === 0}>{busy === 'issue' ? 'Issuing…' : 'Issue'}</button></>}>
          <Field label="System"><input type="text" value={keyName} onChange={(e) => setKeyName(e.target.value)} placeholder="CRM staging" /></Field>
          <Field label="Scopes">
            {TEST_SCOPES.map(([code, label]) => (
              <label key={code} style={{ display: 'flex', gap: 9, alignItems: 'center', padding: '4px 0', fontWeight: 400 }}>
                <input type="checkbox" style={{ width: 'auto' }} checked={scopes.includes(code)}
                  onChange={(e) => setScopes((s) => (e.target.checked ? [...s, code] : s.filter((x) => x !== code)))} />
                {label}
              </label>
            ))}
          </Field>
        </Modal>
      )}

      {issued && (
        <Modal title={`Test key for ${issued.name}`} description="Shown once." onClose={() => setIssued(null)}
          footer={<button className="primary" onClick={() => setIssued(null)}>Done</button>}>
          <pre className="mono keyblock">{issued.key}</pre>
          <button className="small" onClick={() => navigator.clipboard.writeText(issued.key).then(() => toast('Copied.', 'ok'))}>Copy</button>
          <div className="small muted" style={{ marginTop: 10 }}>Send it in the <code className="mono">X-Service-Key</code> header.</div>
        </Modal>
      )}

      {hookSecret && (
        <Modal title="Sandbox webhook secret" description="Shown once." onClose={() => setHookSecret(null)}
          footer={<button className="primary" onClick={() => setHookSecret(null)}>Done</button>}>
          <pre className="mono keyblock">{hookSecret}</pre>
        </Modal>
      )}

      {resetting && sandbox && (
        <Modal title="Reset sandbox" description="Deletes every person, consent and ledger event in the sandbox. Purposes, notices, collection points, test keys and sandbox webhooks stay. Live records are not affected."
          onClose={() => { setResetting(false); setConfirmText(''); }}
          footer={<><button onClick={() => { setResetting(false); setConfirmText(''); }}>Cancel</button><button className="danger" onClick={reset} disabled={busy !== null || confirmText !== 'reset'}>{busy === 'reset' ? 'Resetting…' : 'Reset'}</button></>}>
          <p className="small" style={{ marginTop: 0 }}>{sandbox.people} people and {sandbox.events} ledger events.</p>
          <Field label='Type "reset" to confirm'>
            <input type="text" value={confirmText} onChange={(e) => setConfirmText(e.target.value)} autoComplete="off" />
          </Field>
        </Modal>
      )}
    </Card>
  );
}

/** The four cards, in page order. The allow-list offers test entries once a sandbox exists. */
export function DeveloperTools() {
  const [tick, setTick] = useState(0);
  const sandbox = useResource<{ sandbox: SandboxSummary | null }>(() => api.get('/v1/sandbox'), [tick]);
  return (
    <>
      <RequestLogCard />
      <IdempotencyCard />
      <IpAllowListCard key={sandbox.data?.sandbox ? 'with-sandbox' : 'live-only'} hasSandbox={Boolean(sandbox.data?.sandbox)} />
      <SandboxCard onChange={() => setTick((t) => t + 1)} />
    </>
  );
}
